/**
 * DemoDataTab — Super-Admin Demo Data Generator (iter-54).
 *
 * Populates any selected workspace with realistic test data (employees
 * with hierarchy, students, backdated attendance, mixed-state leaves).
 * Every seeded row is tagged `is_demo:true` so a single reset action
 * wipes it without touching real production records.
 *
 * Ships as a Super-Admin-only tab inside `PlatformAdminPage`.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Warning, Database, Buildings, Users, GraduationCap, ClipboardText,
  Calendar, DownloadSimple, Trash, Sparkle, CheckCircle, CircleNotch,
} from "@phosphor-icons/react";

const DEFAULT_CONFIG = {
  employees: 20,
  students: 30,
  months: 3,
  include_payroll: false,          // MVP: disabled per user directive
  include_leaves: true,
  create_users: true,
};

const COUNT_LABELS = {
  employees:          { label: "Employees",          icon: Users },
  users:              { label: "User accounts",      icon: Users },
  students:           { label: "Students",           icon: GraduationCap },
  attendance:         { label: "Attendance rows",    icon: Calendar },
  leave_requests:     { label: "Leave requests",     icon: ClipboardText },
  approval_requests:  { label: "Live approvals",     icon: ClipboardText },
  branches:           { label: "Branches",           icon: Buildings },
  therapy_types:      { label: "Therapy types",      icon: Sparkle },
  leave_types:        { label: "Leave types",        icon: Sparkle },
  job_levels:         { label: "Job levels",         icon: Sparkle },
  approval_templates: { label: "Approval templates", icon: Sparkle },
};

export default function DemoDataTab() {
  const [workplaces, setWorkplaces] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  // Run state
  const [running, setRunning] = useState(false);
  const [runId, setRunId] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const [summary, setSummary] = useState(null);

  // Reset dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetSummary, setResetSummary] = useState(null);

  const loadWorkplaces = async (preserveSelection = true) => {
    try {
      const { data } = await api.get("/platform/demo-seed/workplaces");
      setWorkplaces(data.workplaces || []);
      if (!preserveSelection && data.workplaces?.length) {
        setSelectedOrgId(data.workplaces[0].id);
      }
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load workspaces"));
    }
  };

  const refreshCounts = async (orgId) => {
    if (!orgId) { setCounts(null); return; }
    try {
      const { data } = await api.get(`/platform/demo-seed/workplaces/${orgId}`);
      setCounts(data.counts || null);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load workspace counts"));
    }
  };

  useEffect(() => { loadWorkplaces(false); }, []);
  useEffect(() => { refreshCounts(selectedOrgId); }, [selectedOrgId]);

  // Poll status while a run is active.
  useEffect(() => {
    if (!runId || !running) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/platform/demo-seed/status/${runId}`);
        setRunStatus(data);
        if (data.status === "complete") {
          setRunning(false);
          setSummary(data.summary || null);
          await Promise.all([loadWorkplaces(), refreshCounts(selectedOrgId)]);
          toast.success("Demo data generated");
        } else if (data.status === "failed") {
          setRunning(false);
          toast.error(`Generation failed: ${data.error || "unknown"}`);
        }
      } catch (e) {
        // transient; keep polling
      }
    };
    tick();
    const iv = setInterval(tick, 1500);
    return () => clearInterval(iv);
  }, [runId, running, selectedOrgId]);

  const selected = useMemo(
    () => workplaces.find(w => w.id === selectedOrgId) || null,
    [workplaces, selectedOrgId],
  );

  const startGenerate = async () => {
    if (!selectedOrgId) { toast.error("Pick a workspace first"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/platform/demo-seed/generate", {
        org_id: selectedOrgId,
        ...config,
      });
      setRunId(data.run_id);
      setRunning(true);
      setRunStatus({ status: "running", step: "Queued", progress_pct: 0 });
      setSummary(null);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to start generation"));
    } finally {
      setLoading(false);
    }
  };

  const downloadCredentials = () => {
    if (!runId) return;
    const url = `${api.defaults.baseURL}/platform/demo-seed/credentials/${runId}.csv`;
    // Trigger native download using auth header via a temporary fetch
    api.get(`/platform/demo-seed/credentials/${runId}.csv`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `demo-credentials-${runId}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(e => toast.error(formatApiError(e, "Download failed")));
  };

  const doReset = async () => {
    if (resetPhrase !== "RESET") { toast.error("Type RESET to confirm"); return; }
    setResetting(true);
    try {
      const { data } = await api.post("/platform/demo-seed/reset", {
        org_id: selectedOrgId,
        confirm: "RESET",
      });
      setResetSummary(data.deleted || {});
      setResetPhrase("");
      setResetOpen(false);
      setSummary(null);
      setRunId(null);
      setRunStatus(null);
      await Promise.all([loadWorkplaces(), refreshCounts(selectedOrgId)]);
      toast.success("Demo data cleared");
    } catch (e) {
      toast.error(formatApiError(e, "Reset failed"));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="demo-data-tab">
      {/* Prominent notice */}
      <div
        role="alert"
        className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md px-4 py-3 flex items-start gap-3"
        data-testid="demo-data-notice"
      >
        <Warning size={22} weight="duotone" className="shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold">Demo Data only</div>
          <div className="text-amber-900/80">
            Everything created here is tagged internally as demo data and can be
            wiped in one click. Real production records are never affected.
          </div>
        </div>
      </div>

      {/* Workplace picker + counts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Buildings size={16} weight="duotone" /> Target workspace
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Label className="text-xs mb-1.5 block">Workspace</Label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger data-testid="demo-workspace-picker">
                <SelectValue placeholder="Pick a workspace…" />
              </SelectTrigger>
              <SelectContent>
                {workplaces.map(w => (
                  <SelectItem key={w.id} value={w.id} data-testid={`demo-workspace-${w.slug}`}>
                    {w.name} <span className="text-muted-foreground text-xs">· {w.slug}</span>
                    {w.has_demo_data && <Badge className="ml-2 bg-amber-100 text-amber-900 border-amber-200">demo</Badge>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {counts && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="demo-counts-grid">
              {Object.entries(counts).map(([key, val]) => {
                const meta = COUNT_LABELS[key] || { label: key, icon: Sparkle };
                const Icon = meta.icon;
                return (
                  <div key={key} className="border rounded-md p-2.5 bg-card">
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Icon size={11} weight="duotone" /> {meta.label}
                    </div>
                    <div className="mt-0.5 text-sm">
                      <span className="font-semibold">{val.real}</span>
                      <span className="text-muted-foreground"> real · </span>
                      <span className={val.demo > 0 ? "font-semibold text-amber-700" : ""}>{val.demo}</span>
                      <span className="text-muted-foreground"> demo</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config panel */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Database size={16} weight="duotone" /> Generator configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Employees ({config.employees})</Label>
            <Input
              type="number" min={5} max={100}
              value={config.employees}
              onChange={e => setConfig({ ...config, employees: Number(e.target.value) })}
              data-testid="demo-cfg-employees"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5">min 5, max 100</div>
          </div>
          <div>
            <Label className="text-xs">Students ({config.students})</Label>
            <Input
              type="number" min={10} max={200}
              value={config.students}
              onChange={e => setConfig({ ...config, students: Number(e.target.value) })}
              data-testid="demo-cfg-students"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5">min 10, max 200</div>
          </div>
          <div>
            <Label className="text-xs">Months of history ({config.months})</Label>
            <Input
              type="number" min={1} max={6}
              value={config.months}
              onChange={e => setConfig({ ...config, months: Number(e.target.value) })}
              data-testid="demo-cfg-months"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5">min 1, max 6</div>
          </div>

          <label className="flex items-start gap-2 md:col-span-3">
            <Checkbox
              checked={config.create_users}
              onCheckedChange={v => setConfig({ ...config, create_users: !!v })}
              data-testid="demo-cfg-create-users"
            />
            <div>
              <div className="text-sm font-medium">Generate employee user accounts</div>
              <div className="text-[11px] text-muted-foreground">
                Creates a login (with password <code className="text-[10px]">Demo@1234</code>) for
                each employee. Roles distributed as 60% self-service, 20% Team Lead, 15% HR Rule Manager, 5% Admin.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-2 md:col-span-3">
            <Checkbox
              checked={config.include_leaves}
              onCheckedChange={v => setConfig({ ...config, include_leaves: !!v })}
              data-testid="demo-cfg-include-leaves"
            />
            <div>
              <div className="text-sm font-medium">Include leave requests (mixed approval states)</div>
              <div className="text-[11px] text-muted-foreground">
                40% approved · 20% rejected · 15% <b>live pending</b> in the workflow engine ·
                15% withdrawn · 10% SLA-escalated.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-2 md:col-span-3 opacity-60 cursor-not-allowed">
            <Checkbox checked={false} disabled data-testid="demo-cfg-include-payroll" />
            <div>
              <div className="text-sm font-medium">Include payroll runs
                <Badge variant="outline" className="ml-2 text-[10px]">Coming soon</Badge>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Skipped in this build. Payroll runs will be seeded in a follow-up iteration.
              </div>
            </div>
          </label>

          <div className="md:col-span-3 flex items-center justify-between pt-2 border-t mt-2">
            <Button
              variant="destructive"
              onClick={() => setResetOpen(true)}
              disabled={!selectedOrgId || running || resetting}
              className="gap-2"
              data-testid="demo-reset-btn"
            >
              <Trash size={14} weight="duotone" /> Reset demo data
            </Button>
            <Button
              onClick={startGenerate}
              disabled={!selectedOrgId || running || loading}
              className="gap-2"
              data-testid="demo-generate-btn"
            >
              {loading || running ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} weight="duotone" />}
              {running ? "Generating…" : "Generate demo data"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live progress + summary */}
      {(running || summary) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              {summary
                ? <CheckCircle size={16} weight="duotone" className="text-emerald-600" />
                : <CircleNotch size={16} className="animate-spin" />}
              {summary ? "Generation complete" : "Generating…"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {runStatus && (
              <div className="space-y-1.5" data-testid="demo-run-status">
                <div className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>{runStatus.step}</span>
                  <span>{runStatus.progress_pct || 0}%</span>
                </div>
                <Progress value={runStatus.progress_pct || 0} />
              </div>
            )}
            {summary && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="demo-summary-grid">
                  <SummaryTile label="Employees"       value={summary.employees} />
                  <SummaryTile label="User accounts"   value={summary.users} />
                  <SummaryTile label="Students"        value={summary.students} />
                  <SummaryTile label="Attendance rows" value={summary.attendance_records} />
                  <SummaryTile label="Leaves total"    value={summary.leaves_total} />
                  <SummaryTile label="Pending (live)"  value={summary.leaves_by_state?.pending ?? 0} tone="warning" />
                  <SummaryTile label="Approved"        value={summary.leaves_by_state?.approved ?? 0} />
                  <SummaryTile label="Rejected"        value={summary.leaves_by_state?.rejected ?? 0} />
                </div>
                {summary.credentials?.length > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-xs text-muted-foreground">
                      {summary.credentials.length} user credential(s) available for download.
                    </div>
                    <Button
                      size="sm" variant="outline"
                      onClick={downloadCredentials}
                      className="gap-1.5"
                      data-testid="demo-download-credentials"
                    >
                      <DownloadSimple size={14} weight="duotone" /> Download credentials CSV
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reset summary */}
      {resetSummary && (
        <Card data-testid="demo-reset-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Reset complete — deleted counts</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Collection</TableHead>
                  <TableHead className="text-right">Deleted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(resetSummary).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell className="text-xs">{k}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{v}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Reset confirmation dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Warning size={18} weight="duotone" /> Reset demo data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              This permanently deletes every record in <b>{selected?.name || "the workspace"}</b>{" "}
              that is tagged as demo data — employees, users, students, attendance,
              leaves, live approvals, and any placeholder branches / therapy types
              / leave types / job levels created by the seeder.
            </p>
            <p className="text-muted-foreground">Real records will not be touched.</p>
            <div>
              <Label className="text-xs">Type <code className="text-[11px]">RESET</code> to confirm</Label>
              <Input
                value={resetPhrase}
                onChange={e => setResetPhrase(e.target.value)}
                placeholder="RESET"
                data-testid="demo-reset-phrase"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetOpen(false); setResetPhrase(""); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={doReset}
              disabled={resetPhrase !== "RESET" || resetting}
              data-testid="demo-reset-confirm"
            >
              {resetting ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null}
              Delete demo data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  return (
    <div className={"border rounded-md p-2.5 " + (tone === "warning" ? "bg-amber-50 border-amber-200" : "bg-card")}>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
